import React, { useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import {
  ADMIN_REVIEW_PENDING,
  ADMIN_REVIEW_REJECTED,
  CUSTOMER_REVIEW_ACTION_ENABLE_PROCESSING,
  CUSTOMER_REVIEW_APPROVED,
  CUSTOMER_REVIEW_PENDING,
  CUSTOMER_REVIEW_REJECTED,
  STATUS_PENDING,
} from 'constants/index';
import MadeToOrderUploadModal from './MadeToOrderUploadModal';

/**
 * Made to order orders cannot be accepted until the customer approves the
 * product images the seller adds. The seller uploads them here or in the app,
 * once per order; after that this shows the review state read only.
 */
const MadeToOrderNotice = ({ order, showImages = false, refreshOrders = () => {} }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  if (!order?.customer_review_required) {
    return null;
  }
  const customerReview = order?.customerReview;
  const customerResponse = customerReview?.customerResponse;
  const adminReviewStatus = customerReview?.adminReviewStatus;
  const isImageReviewPending = adminReviewStatus === ADMIN_REVIEW_PENDING;
  const isSentBack = adminReviewStatus === ADMIN_REVIEW_REJECTED;
  // The seller submits once, unless the Danube team sends the images back.
  const canUploadImages = (!customerReview || isSentBack) && Number(order?.order_status) === STATUS_PENDING;

  return (
    <div className="mb-2">
      <Badge bg="outline-primary" className="me-1">
        Made To Order
      </Badge>
      <If condition={!customerReview}>
        <div className="text-small text-muted mt-1">Add photos of the actual product. The order can be accepted only after the customer approves them.</div>
      </If>
      <If condition={isSentBack}>
        <div className="text-small text-danger mt-1">The Danube team sent the photos back. Please add a new set.</div>
        <If condition={customerReview?.adminReviewRemarks}>
          <div className="text-small text-muted">Remarks: {customerReview?.adminReviewRemarks}</div>
        </If>
      </If>
      <If condition={isImageReviewPending}>
        <div className="text-small text-warning mt-1">Product photos submitted. The Danube team is reviewing them.</div>
      </If>
      <If condition={canUploadImages}>
        <Button size="sm" variant="outline-primary" className="mt-2" onClick={() => setShowUploadModal(true)}>
          {isSentBack ? 'Add New Product Photos' : 'Add Product Photos'}
        </Button>
        <MadeToOrderUploadModal show={showUploadModal} order={order} onHide={() => setShowUploadModal(false)} onComplete={refreshOrders} />
      </If>
      <If condition={customerResponse === CUSTOMER_REVIEW_PENDING && !isImageReviewPending && !isSentBack}>
        <div className="text-small text-warning mt-1">Product photos submitted. Waiting for the customer to approve them.</div>
      </If>
      <If condition={customerResponse === CUSTOMER_REVIEW_REJECTED}>
        <If condition={customerReview?.adminAction === CUSTOMER_REVIEW_ACTION_ENABLE_PROCESSING}>
          <div className="text-small text-success mt-1">
            The customer rejected the product photos, but the Danube team enabled order processing. The order can be accepted.
          </div>
        </If>
        <If condition={customerReview?.adminAction !== CUSTOMER_REVIEW_ACTION_ENABLE_PROCESSING}>
          <div className="text-small text-danger mt-1">
            The customer rejected the product photos. The Danube team has to decide whether the order is processed anyway or cancelled.
          </div>
        </If>
        <If condition={customerReview?.customerRemarks}>
          <div className="text-small text-muted">Customer remarks: {customerReview?.customerRemarks}</div>
        </If>
      </If>
      <If condition={customerResponse === CUSTOMER_REVIEW_APPROVED}>
        <div className="text-small text-success mt-1">The customer approved the product photos.</div>
      </If>
      <If condition={showImages && customerReview?.images?.length}>
        <div className="d-flex flex-wrap gap-1 mt-2">
          {(customerReview?.images || []).map((image) => (
            <a key={image} href={`https:${image}`} target="_blank" rel="noreferrer">
              <img src={`https:${image}`} className="img-fluid rounded-md" alt="product" width="80" height="80" />
            </a>
          ))}
        </div>
      </If>
    </div>
  );
};

export default MadeToOrderNotice;
